import React, { useState } from 'react';
import { Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import { officeLocations } from '../mockData';
import { BRAND, waLink } from './site/brand';
import { submitEnquiry } from './site/api';
import HoneyPot from './site/HoneyPot';

const EMPTY = { name: '', email: '', phone: '', destination: '', message: '' };
const DESTINATIONS = ['India', 'Singapore', 'Malaysia', 'Dubai', 'Maldives', 'Europe', 'USA'];

/** Home-page enquiry block: quote form + contact details + office addresses. */
const Contact = () => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ busy: false, ok: false, err: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, ok: false, err: '' });
    try {
      await submitEnquiry({
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(),
        destination: f.destination, message: f.message, packageTitle: 'Website home page enquiry',
      });
      setState({ busy: false, ok: true, err: '' });
      setF(EMPTY);
    } catch {
      setState({ busy: false, ok: false, err: `Sorry, that didn't go through. Please WhatsApp or call ${BRAND.phone}.` });
    }
  };

  return (
    <div className="vk-home">
      <section id="contact" className="vk-section" style={{ scrollMarginTop: 'calc(var(--header-h) + 12px)' }}>
        <div className="vk-wrap vk-split vk-split--form">
          <div className="vk-panel" style={{ padding: 28 }}>
            <p className="vk-kicker">Get in touch</p>
            <h2 className="vk-display" style={{ fontSize: 'clamp(30px,4vw,44px)', margin: '0 0 20px' }}>Get a custom quote</h2>
            {state.ok ? (
              <div className="vk-alert vk-alert--ok" role="status">Thank you — your enquiry has reached our team. We'll contact you within 24 hours.</div>
            ) : (
              <form className="vk-form" onSubmit={submit}><HoneyPot />
                <div className="vk-form__row">
                  <div className="vk-field"><label htmlFor="h-name">Full name *</label><input id="h-name" required value={f.name} onChange={set('name')} autoComplete="name" disabled={state.busy} /></div>
                  <div className="vk-field"><label htmlFor="h-phone">Phone *</label><input id="h-phone" required type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" placeholder="+91 XXXXX XXXXX" disabled={state.busy} /></div>
                </div>
                <div className="vk-form__row">
                  <div className="vk-field"><label htmlFor="h-email">Email *</label><input id="h-email" required type="email" value={f.email} onChange={set('email')} autoComplete="email" disabled={state.busy} /></div>
                  <div className="vk-field">
                    <label htmlFor="h-dest">Destination</label>
                    <select id="h-dest" value={f.destination} onChange={set('destination')} disabled={state.busy}>
                      <option value="">Choose destination</option>
                      {DESTINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div className="vk-field"><label htmlFor="h-msg">Message</label><textarea id="h-msg" rows={4} value={f.message} onChange={set('message')} placeholder="Tell us about your travel plans…" disabled={state.busy} /></div>
                {state.err ? <div className="vk-alert vk-alert--err" role="alert">{state.err}</div> : null}
                <button type="submit" className="vk-btn vk-btn--hot" disabled={state.busy}>{state.busy ? 'Sending…' : 'Send enquiry'}</button>
              </form>
            )}
          </div>

          <div style={{ display: 'grid', gap: 14 }}>
            <div className="vk-panel vk-panel--sand">
              <h3>Contact us</h3>
              <ul className="vk-ticks">
                <li><Phone size={18} color="var(--hot)" /> <span><a href={BRAND.phoneLink} data-testid="contact-mobile-phone">{BRAND.phone}</a> (mobile & WhatsApp)</span></li>
                <li><Phone size={18} color="var(--hot)" /> <span><a href={BRAND.landlineLink} data-testid="contact-landline-phone">{BRAND.landline}</a> (office)</span></li>
                <li><Mail size={18} color="var(--hot)" /> <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a></li>
                <li><MessageCircle size={18} color="var(--hot)" /> <a href={waLink()} target="_blank" rel="noopener noreferrer">Chat on WhatsApp</a></li>
              </ul>
            </div>
            <div className="vk-panel">
              <h3>Our offices</h3>
              <ul className="vk-ticks" style={{ gap: 16 }}>
                {officeLocations.map((o) => (
                  <li key={o.country}>
                    <MapPin size={18} color="var(--hot)" />
                    <span style={{ lineHeight: 1.5 }}>
                      <strong>{o.country} office</strong><br />
                      <span style={{ color: 'var(--muted)' }}>{o.name}<br />{o.address}</span>
                      {o.phone ? <><br /><a href={`tel:${o.phone.replace(/\s/g, '')}`}>{o.phone}</a>{o.landline ? <span style={{ color: 'var(--muted)' }}> · landline {o.landline}</span> : null}</> : null}
                      {o.ein || o.registration || o.gst ? <><br /><small style={{ color: 'var(--muted)' }}>{o.ein || o.registration || o.gst}</small></> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
